import { useEffect, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { PAYMENTS_ENABLED, formatListingPublishedAt, isListingReserved, isPlatformVerifiedListing, reservationBlocksBuyer, type SellerPublicSummary } from '@redevicer/shared'
import { listingsAPI } from '../api/client'
import type { BrowsePreset } from './MarketplaceHome'
import OfferModal from './OfferModal'
import ExchangeOfferModal from './ExchangeOfferModal'
import ListingCardVerifiedBadge from './ListingCardVerifiedBadge'
import CompareBar from './CompareBar'
import CompareModal from './CompareModal'
import { useCart } from '../hooks/useCart'
import { useFavorites } from '../hooks/useFavorites'
import { useCompare } from '../hooks/useCompare'
import { IconCart } from './AppNavIcons'
import ListingFavoriteButton from './ListingFavoriteButton'
import ListingCardSellerLine from './ListingCardSellerLine'
import ListingCardBatteryBadge from './ListingCardBatteryBadge'
import { listingCardPhotoUrls } from '../utils/listingThumb'
import ListingCardPhotoCarousel from './ListingCardPhotoCarousel'
import { inBoxLabelKey } from '../utils/inBoxCompleteness'
import { prefetchListing } from '../utils/listingDetailCache'
import { listingPublicPath } from '../utils/listingPath'
import { blocksOwnListingAction } from '../utils/testDeals'
import ListingCardSoldOverlay, { listingCardSoldCoverClass } from './ListingCardSoldOverlay'

interface ListingImage {
  id: number
  thumbnail_url: string
  display_url?: string
  is_primary: boolean
}

interface ListingRow {
  id: number
  title: string
  price: number
  condition: string
  created_at?: string
  in_box_completeness?: string
  battery_health?: number
  parts_authenticity?: Record<string, { status?: string; grade?: string | null }> | null
  photo_quality?: string | null
  photo_suggested_condition?: string | null
  model_match?: string | null
  imei?: string | null
  imei_verified?: boolean
  ai_recommended_eur?: number | null
  seller_city?: string | null
  seller?: SellerPublicSummary | null
  shipment_method?: string
  owner_id: number
  is_sold?: boolean
  is_reserved?: boolean
  reserved_for_user_id?: number | null
  phone_model: { manufacturer: string; model_name: string; hero_image_path?: string | null; release_year?: number | null }
  images?: ListingImage[]
}

interface FeaturedListingsRailProps {
  onOpenCatalog: (preset?: BrowsePreset) => void
  onOpenListing: (listingId: number) => void
  token: string | null
  currentUserId?: number
  onRequireLogin?: (intent?: 'offer' | 'exchange' | null, listingId?: number) => void
  onBuyNow?: (listingId: number) => void
}

export default function FeaturedListingsRail({
  onOpenCatalog,
  onOpenListing,
  token,
  currentUserId,
  onRequireLogin,
  onBuyNow,
}: FeaturedListingsRailProps) {
  const { t, i18n } = useTranslation()
  const { has, toggle } = useFavorites()
  const { add: addToCart } = useCart()
  const { has: hasCompare, toggle: toggleCompare, max: maxCompare } = useCompare()
  const [items, setItems] = useState<ListingRow[]>([])
  const [loading, setLoading] = useState(true)
  const [offerListing, setOfferListing] = useState<ListingRow | null>(null)
  const [exchangeListing, setExchangeListing] = useState<ListingRow | null>(null)
  const [compareOpen, setCompareOpen] = useState(false)

  useEffect(() => {
    listingsAPI
      .getListings(0, 24)
      .then((res) =>
        setItems(
          Array.isArray(res.data)
            ? res.data.filter((l: ListingRow) => Number(l.price) > 0)
            : [],
        ),
      )
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }, [])

  const isOwnBlocked = (listing: ListingRow) => blocksOwnListingAction(listing.owner_id, currentUserId)

  function putInCart(listing: ListingRow, e: MouseEvent) {
    e.stopPropagation()
    const line = { listing_id: listing.id, owner_id: listing.owner_id }
    const result = addToCart(line)
    if (result === 'invalid') {
      alert(t('cart.addFailed'))
      return
    }
    if (result === 'duplicate') {
      alert(t('cart.alreadyIn'))
      return
    }
    alert(t('cart.added'))
  }

  if (!loading && items.length === 0) {
    return null
  }

  return (
    <section className="vc-section vc-featured-section vc-featured-section--wide" aria-labelledby="vc-featured-heading">
      <div className="vc-section-head">
        <h3 id="vc-featured-heading" className="vc-section-title">
          {t('vc.featured.title')}
        </h3>
        <button type="button" className="vc-section-link" onClick={() => onOpenCatalog()}>
          {t('vc.shopAll')}
        </button>
      </div>

      {loading ? (
        <div className="vc-featured-rail vc-featured-rail--loading" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="vc-featured-skeleton" />
          ))}
        </div>
      ) : (
        <div className="vc-featured-rail" role="list">
          {items.map((listing) => {
            const cardPhotos = listingCardPhotoUrls(listing)
            return (
              <article
                key={listing.id}
                className={`listing-card listing-card--clickable${listing.is_sold ? ' listing-card--sold' : ''}`}
                role="listitem"
              >
                <div
                  className={listingCardSoldCoverClass(
                    `listing-card-cover listing-card-hit${cardPhotos.isUserPhoto ? ' listing-card-cover--user' : ''}`,
                    listing.is_sold,
                  )}
                  tabIndex={0}
                  onMouseEnter={() => prefetchListing(listing.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onOpenListing(listing.id)
                    }
                  }}
                >
                  <ListingCardPhotoCarousel
                    urls={cardPhotos.urls}
                    isUserPhoto={cardPhotos.isUserPhoto}
                    onTap={() => onOpenListing(listing.id)}
                    onPrefetch={() => prefetchListing(listing.id)}
                  />
                  <ListingCardSoldOverlay show={listing.is_sold} />
                  {isListingReserved(listing) && !listing.is_sold ? (
                    <span className="reserved-badge listing-card-cover__reserved">{t('listing.reservedBadge')}</span>
                  ) : null}
                  <div className="listing-card-cover__actions">
                    <button
                      type="button"
                      className={`listing-compare-btn${hasCompare(listing.id) ? ' listing-compare-btn--on' : ''}`}
                      aria-label={hasCompare(listing.id) ? t('compare.remove') : t('compare.add')}
                      aria-pressed={hasCompare(listing.id)}
                      onClick={(e) => {
                        e.stopPropagation()
                        const r = toggleCompare(listing.id)
                        if (r === 'full') alert(t('compare.maxReached', { max: maxCompare }))
                      }}
                    >
                      <span aria-hidden="true">⇄</span>
                      <span className="listing-compare-btn__label">
                        {hasCompare(listing.id) ? t('compare.inCompare') : t('compare.toggleShort')}
                      </span>
                    </button>
                    <div className="listing-card-cover__actions-end">
                      {!listing.is_sold && !isOwnBlocked(listing) && PAYMENTS_ENABLED ? (
                        <button
                          type="button"
                          className="listing-card-cart-btn"
                          aria-label={t('cart.addToCart')}
                          title={t('cart.addToCart')}
                          onClick={(e) => putInCart(listing, e)}
                        >
                          <IconCart width={16} height={16} />
                        </button>
                      ) : null}
                      {token ? (
                        <ListingFavoriteButton
                          active={has(listing.id)}
                          label={has(listing.id) ? t('vc.favorites.remove') : t('vc.favorites.add')}
                          onClick={(e) => {
                            e.stopPropagation()
                            toggle(listing.id)
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="listing-card-body">
                  <a
                    className="listing-card-hit"
                    href={listingPublicPath(listing.id, i18n.language)}
                    onMouseEnter={() => prefetchListing(listing.id)}
                    onPointerDown={() => prefetchListing(listing.id)}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                      e.preventDefault()
                      onOpenListing(listing.id)
                    }}
                  >
                    <ListingCardSellerLine seller={listing.seller} sellerCity={listing.seller_city} />
                    <h3>{listing.phone_model.manufacturer}</h3>
                    <p className="model-name">{listing.phone_model.model_name}</p>
                    <p className="title">{listing.title}</p>
                    <div className="listing-card-extra">
                      {listing.created_at ? (
                        <time className="listing-card-date" dateTime={listing.created_at}>
                          {formatListingPublishedAt(listing.created_at, i18n.language)}
                        </time>
                      ) : null}
                      {listing.in_box_completeness ? (
                        <p className="listing-kit-badge">{t(inBoxLabelKey(listing.in_box_completeness))}</p>
                      ) : null}
                    </div>
                    <div className="listing-card-meta">
                      <div className="listing-card-price-row">
                        <p className="price">€{listing.price.toFixed(2)}</p>
                        {isPlatformVerifiedListing(listing) ? <ListingCardVerifiedBadge /> : null}
                      </div>
                      <div className="listing-card-badges">
                        <ListingCardBatteryBadge health={listing.battery_health} />
                      </div>
                    </div>
                  </a>
                  {listing.is_sold ? (
                    <p className="listing-own-hint">{t('listing.detailSold')}</p>
                  ) : isOwnBlocked(listing) ? (
                    <p className="listing-own-hint">{t('catalog.cannotOfferOwn')}</p>
                  ) : reservationBlocksBuyer(listing, currentUserId) ? (
                    <p className="listing-own-hint">{t('listing.detailReservedOther')}</p>
                  ) : (
                    <div className="listing-card-actions listing-card-actions--catalog listing-card-actions--rail">
                      <button
                        type="button"
                        className="listing-card-btn listing-card-btn--buy listing-card-btn--rail-primary"
                        onClick={(e) => {
                          e.stopPropagation()
                          onOpenListing(listing.id)
                        }}
                      >
                        {t('catalog.viewShort')}
                      </button>
                      <button
                        type="button"
                        className="listing-card-btn listing-card-btn--exchange"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!token) {
                            onRequireLogin?.('exchange', listing.id)
                            return
                          }
                          setExchangeListing(listing)
                        }}
                        title={t('catalog.exchangeOfferHint')}
                      >
                        {t('catalog.exchange')}
                      </button>
                      <button
                        type="button"
                        className="listing-card-btn listing-card-btn--offer"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!token) {
                            onRequireLogin?.('offer', listing.id)
                            return
                          }
                          setOfferListing(listing)
                        }}
                      >
                        {t('catalog.offerShort')}
                      </button>
                    </div>
                  )}
                </div>
              </article>
            )
          })}
        </div>
      )}

      {offerListing && token && (
        <OfferModal
          listingId={offerListing.id}
          currentPrice={offerListing.price}
          token={token}
          isOpen={Boolean(offerListing)}
          onClose={() => setOfferListing(null)}
          overlayClassName="modal-overlay--stack-top"
        />
      )}

      {exchangeListing && token && (
        <ExchangeOfferModal
          listingId={exchangeListing.id}
          currentPrice={exchangeListing.price}
          isOpen={Boolean(exchangeListing)}
          token={token}
          onClose={() => setExchangeListing(null)}
          overlayClassName="modal-overlay--stack-top"
        />
      )}

      <CompareBar onOpen={() => setCompareOpen(true)} />
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        onOpenListing={(id) => { setCompareOpen(false); onOpenListing(id) }}
      />
    </section>
  )
}
